/**
 * Reserva gratuita: cota pequena que recarrega a cada `window_hours` e só é gasta quando o
 * crédito pago chega a zero. NÃO é o "tanque" do medidor — `tank_size_tokens` continua sendo
 * a escala do crédito pago, e os dois nomes convivem por decisão do backend. Na UI isto é
 * "reserva"; ver docs/handoff-front-web-tanque-free.md na ftcloud-api.
 */
export interface AiFreeTank {
  /** true = a reserva é a fonte de gasto AGORA. Vem resolvido do backend — não derivar de `credits_total == 0`. */
  active: boolean;
  /** Tamanho da reserva NESTA janela. Pode mudar entre janelas — nunca tratar como constante. */
  amount_total: number;
  /** Pode vir NEGATIVO: o débito acontece depois da resposta, então o último turno da janela pode estourar. */
  amount_remaining: number;
  /** `amount_total - amount_remaining`. Pode passar de `amount_total` pelo mesmo motivo. */
  used_this_window: number;
  window_hours: number;
  /** null = a janela ainda não começou; ela nasce no primeiro consumo, não quando a tela abre. */
  window_start: string | null;
  /** null junto com `window_start` — sem janela aberta não há contagem regressiva pra mostrar. */
  refill_at: string | null;
}

/**
 * Estados da reserva, na ordem da árvore de decisão do contrato. Existe pra que a decisão
 * seja tomada uma vez e nos mesmos termos nas três telas (aba de consumo, sidebar, chat).
 */
export type FreeTankState = 'off' | 'idle' | 'prospective' | 'available' | 'empty';

export function freeTankState(tank: AiFreeTank | null | undefined): FreeTankState {
  if (!tank) return 'off';
  if (!tank.active) return 'idle';
  if (!tank.refill_at) return 'prospective';
  return tank.amount_remaining > 0 ? 'available' : 'empty';
}

/**
 * A reserva é a fonte de gasto AGORA — os três estados em que ela manda na tela. Com 'off'
 * (kill switch) ou 'idle' o medidor de crédito pago continua sendo o assunto.
 */
/**
 * Nomes de plano sem assinatura paga.
 *
 * FALLBACK DE TRANSIÇÃO: vale só enquanto `is_paid` não chega de todo ambiente. Comparar
 * nome é frágil — um "Free Trial" ou um nome localizado passaria por pago. Pode ser
 * removido junto com o ramo de fallback assim que a API estiver publicada em toda parte.
 */
const FREE_PLAN_NAMES: ReadonlySet<string> = new Set(['free', 'gratuito', 'gratis', 'grátis']);

function isFreePlanName(plan: string | null | undefined): boolean {
  const name = (plan ?? '').trim().toLowerCase();
  return name === '' || FREE_PLAN_NAMES.has(name);
}

/**
 * Se a tela deve oferecer a compra de tanque extra.
 *
 * Duas condições, e as duas são necessárias: plano pago E assinatura ativa. Plano pago
 * expirado ou com pagamento pendente não compra até regularizar.
 *
 * Nome vazio bloqueia (falha fechada). Mostrar o botão a quem não pode comprar é o defeito
 * que se está corrigindo; escondê-lo de um pagante é visível e reversível na hora.
 *
 * Mora aqui, e não num getter de componente, porque a mesma decisão é tomada na aba de
 * consumo e na sidebar do dashboard — o bug original veio de cada tela ter a sua versão.
 */
/**
 * Se a conta está num plano pago — independentemente de a assinatura estar vigente.
 *
 * Separado de `canBuyExtraTokens` porque as duas perguntas são diferentes: comprar avulso
 * exige assinatura ativa, mas saber que existe uma cota mensal que renova vale também para
 * quem venceu ou esgotou. Era a pergunta que `hasMonthlyQuota` tentava responder por
 * `credits_limit > 0`, e errava: o Free chega com 10000.
 *
 * A flag do backend é a autoridade quando existe; o nome só cobre o intervalo de
 * implantação. `typeof` em vez de `??` porque `is_paid: false` é resposta, não ausência.
 */
export function isPaidPlan(
  usage: Pick<AiUsageResponse, 'plan' | 'is_paid'> | null | undefined,
): boolean {
  if (!usage) return false;
  if (typeof usage.is_paid === 'boolean') return usage.is_paid;
  return !isFreePlanName(usage.plan);
}

export function canBuyExtraTokens(
  usage: Pick<AiUsageResponse, 'plan' | 'is_paid'> | null | undefined,
  planStatus: string | null | undefined,
): boolean {
  return isPaidPlan(usage) && planStatus === 'active';
}

/**
 * Se a lista de regras deve explicar o tanque cortesia.
 *
 * Vale sempre que a conta tem o tanque, inclusive ocioso (`idle`) — a regra é informativa e
 * responde "o que acontece quando meus tanques acabarem". Amarrá-la a `isFreeTankInUse` a
 * escondia de quem ainda tinha tanque, ou seja, de todo mundo que ainda podia se planejar.
 * Só some com o kill switch (`off`), quando a conta não tem cortesia nenhuma para explicar.
 */
export function shouldShowReserveRule(tank: AiFreeTank | null | undefined): boolean {
  return freeTankState(tank) !== 'off';
}

export function isFreeTankInUse(tank: AiFreeTank | null | undefined): boolean {
  const state = freeTankState(tank);
  return state === 'prospective' || state === 'available' || state === 'empty';
}

/** Nível do galão da reserva, 0..100. O estouro clampa a barra, não o número (ver `freeTankRemainingDisplay`). */
export function freeTankFillPercent(tank: AiFreeTank | null | undefined): number {
  if (!tank || tank.amount_total <= 0) return 0;
  const ratio = tank.amount_remaining / tank.amount_total;
  return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
}

/** Créditos a exibir. O estouro vira 0: a dívida não atravessa a janela, então mostrá-la confundiria. */
export function freeTankRemainingDisplay(tank: AiFreeTank | null | undefined): number {
  return Math.max(0, tank?.amount_remaining ?? 0);
}

/** Minutos até o refill; null quando a janela ainda não começou e não há o que contar. */
export function freeTankMinutesToRefill(tank: AiFreeTank | null | undefined, now: Date = new Date()): number | null {
  if (!tank?.refill_at) return null;
  const diffMs = new Date(tank.refill_at).getTime() - now.getTime();
  return Math.max(0, Math.floor(diffMs / 60000));
}

/**
 * Horário do refill em HH:mm, no fuso do usuário. Usa 24h fixo: o resto da tela de créditos
 * já formata assim, e "16h20" é a leitura pedida pelo contrato. Vazio quando não há refill.
 */
export function formatRefillTime(refillAt: string | null | undefined, timeZone?: string): string {
  if (!refillAt) return '';
  const date = new Date(refillAt);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone,
  }).format(date);
}

/** Origem do crédito de um lançamento do extrato. Os dois ids nunca vêm preenchidos juntos. */
export function historyCreditOrigin(item: AiUsageHistoryItem): 'reserve' | 'purchase' | 'plan' {
  if (item.free_tank_id) return 'reserve';
  if (item.token_purchase_lot_id) return 'purchase';
  return 'plan';
}

export interface AiUsageResponse {
  plan: string;
  /**
   * Plano pago, dito pelo backend. Opcional porque está em implantação — enquanto não
   * chega, `canBuyExtraTokens` cai no nome do plano.
   */
  is_paid?: boolean;
  billing_anchor_day: number;
  credits_used_this_period: number;
  credits_limit: number;
  credits_total: number;
  next_expiring_amount: number | null;
  next_expiring_at: string | null;
  period_id: string;
  period_start: string;
  period_end: string;
  /** Tamanho de um "tanque" em créditos, pra exibição em tanques cheios + % do tanque atual. Campo novo — pode vir ausente/null/0 em ambientes/planos antigos. */
  tank_size_tokens?: number | null;
  /** null = feature desligada no backend (kill switch): não renderizar nada da reserva. */
  free_tank?: AiFreeTank | null;
}

export interface AiUsageHistoryItem {
  transaction_id: string;
  amount: number;
  type: string;
  reference_id: string;
  created_date: string;
  /** Lote da compra que originou o crédito. Null nos movimentos que não vêm de compra. */
  token_purchase_lot_id?: string | null;
  /** Reserva gratuita que originou o débito. Nunca vem junto com `token_purchase_lot_id`. */
  free_tank_id?: string | null;
  /**
   * Conversa que gerou o débito. Só serve pra exibição/link — null em lançamento que não é
   * de chat, em histórico anterior à coleta desse vínculo, e se a sessão foi apagada.
   */
  session_id?: string | null;
  /** Mensagem dentro da sessão. Mesmas condições de null do session_id. */
  user_message_id?: string | null;
  /**
   * Primeiros 60 caracteres da pergunta que gerou o débito, já cortados no servidor e SEM
   * reticências. É best effort: vem null se a busca falhar, sem derrubar o histórico.
   * Ver ftcloud-api/docs/integracoes/frontend-angular/ANGULAR_AI_USAGE_HISTORY_PREVIEW.md.
   */
  query_preview?: string | null;
}

export interface AiUsageHistoryResponse {
  items: AiUsageHistoryItem[];
  total_count: number;
  page: number;
  page_size: number;
  /** Tamanho de um "tanque" em créditos, pra exibir cada movimento em % do tanque. Campo novo — pode vir ausente/null/0 em ambientes/planos antigos. */
  tank_size_tokens?: number | null;
}
